import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Email,
  fetchIncomingEmails,
  fetchIncomingEmailsByEmail,
  updateEmailStatus,
} from '../../services/emailService';
import './UserMenu.css';

const UserMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState<Email[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const userId = user?.id || '';
  const userEmail = user?.email || user?.contactDetails?.personalEmail || user?.contactDetails?.email || '';

  const loadNotifications = useCallback(async () => {
    if (!userId && !userEmail) {
      setNotificationEmails([]);
      setNotificationsLoading(false);
      return;
    }

    setNotificationsLoading(true);
    try {
      const page = userId
        ? await fetchIncomingEmails(userId, 0, 8)
        : await fetchIncomingEmailsByEmail(userEmail, 0, 8);
      setNotificationEmails(page.content || []);
      setNotificationsError('');
    } catch (error) {
      setNotificationsError(error instanceof Error ? error.message : 'Failed to load notifications.');
    } finally {
      setNotificationsLoading(false);
    }
  }, [userEmail, userId]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (showNotifications) loadNotifications();
  }, [loadNotifications, showNotifications]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    setShowNotifications(false);
  };

  const closeMenu = () => setIsOpen(false);

  const toggleNotifications = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowNotifications((current) => !current);
    setIsOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    closeMenu();
  };

  const isUnread = (email: Email) => String(email.status || '').toUpperCase() === 'UNREAD';
  const unreadCount = notificationEmails.filter(isUnread).length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (email: Email) => {
    if (isUnread(email) && userId) {
      setNotificationEmails((prev) =>
        prev.map((item) => (item.id === email.id ? { ...item, status: 'READ' } : item))
      );
      await updateEmailStatus(email.id, userId, 'READ').catch((error) => {
        setNotificationsError(error instanceof Error ? error.message : 'Failed to mark notification read.');
      });
    }

    setShowNotifications(false);
    navigate(`/account/inbox/${email.id}`);
  };

  const markAllNotificationsRead = async () => {
    const unreadEmails = notificationEmails.filter(isUnread);
    if (unreadEmails.length === 0) return;

    if (!userId) {
      setNotificationsError('Open the inbox to refresh notification status.');
      return;
    }

    setNotificationEmails((prev) =>
      prev.map((email) => (isUnread(email) ? { ...email, status: 'READ' } : email))
    );

    const failures = await Promise.allSettled(
      unreadEmails.map((email) => updateEmailStatus(email.id, userId, 'READ'))
    );
    if (failures.some((result) => result.status === 'rejected')) {
      setNotificationsError('Some notifications could not be marked read.');
      loadNotifications();
    } else {
      setNotificationsError('');
    }
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user?.firstName) {
      return user.firstName[0].toUpperCase();
    }
    if (user?.username) {
      return user.username[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="user-menu" ref={menuRef}>
      <button className="notification-bell" onClick={toggleNotifications} aria-label="Notifications">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>

      {showNotifications && (
        <div className="notifications-dropdown">
          <div className="notifications-header">
            <h4>Notifications</h4>
            <button
              className="mark-all-read"
              type="button"
              onClick={markAllNotificationsRead}
              disabled={unreadCount === 0 || notificationsLoading}
            >
              Mark all read
            </button>
          </div>
          <div className="notifications-list">
            {notificationsLoading ? (
              <div className="no-notifications">Loading notifications...</div>
            ) : notificationsError ? (
              <div className="notifications-error">{notificationsError}</div>
            ) : notificationEmails.length === 0 ? (
              <div className="no-notifications">No email notifications yet</div>
            ) : (
              notificationEmails.map((email) => (
                <button
                  key={email.id}
                  type="button"
                  className={`notification-item ${isUnread(email) ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(email)}
                >
                  <div className="notification-message">{formatNotificationTitle(email)}</div>
                  <div className="notification-subject">{email.title || 'No subject'}</div>
                  <div className="notification-time">{formatEmailTime(email.timestamp)}</div>
                </button>
              ))
            )}
          </div>
          <Link className="notifications-footer" to="/account/inbox" onClick={() => setShowNotifications(false)}>
            View inbox
          </Link>
        </div>
      )}

      <button className="user-menu-trigger" onClick={toggleMenu} aria-label="User menu">
        <div className="user-avatar">
          {user?.picture ? (
            <img src={user.picture} alt={user.firstName || 'User'} />
          ) : (
            <span className="avatar-initials">{getInitials()}</span>
          )}
        </div>
        <span className="user-name">{user?.firstName || user?.username || 'Account'}</span>
        <svg className={`dropdown-arrow ${isOpen ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="user-dropdown">
          <div className="dropdown-header">
            <div className="dropdown-avatar">
              {user?.picture ? (
                <img src={user.picture} alt={user.firstName || 'User'} />
              ) : (
                <span className="avatar-initials-large">{getInitials()}</span>
              )}
            </div>
            <div className="dropdown-user-info">
              <div className="dropdown-name">{user?.firstName} {user?.lastName}</div>
              <div className="dropdown-email">{user?.email}</div>
            </div>
          </div>

          <div className="dropdown-divider" />

          <div className="dropdown-links">
            <Link to="/account/profile" onClick={closeMenu} className="dropdown-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              My Profile
            </Link>

            <Link to="/account/tasks" onClick={closeMenu} className="dropdown-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              My Tasks
            </Link>

            <Link to="/account/payments" onClick={closeMenu} className="dropdown-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                <line x1="1" y1="10" x2="23" y2="10" />
              </svg>
              Payments &amp; Credits
            </Link>

            <Link to="/account/inbox" onClick={closeMenu} className="dropdown-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 12h-6l-2 3h-4l-2-3H2" />
                <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
              </svg>
              Inbox
            </Link>

            <Link to="/account/business/my" onClick={closeMenu} className="dropdown-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
              </svg>
              Business Profile
            </Link>

            <Link to="/account/help-support" onClick={closeMenu} className="dropdown-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              Help & Support
            </Link>

            <Link to="/account/settings" onClick={closeMenu} className="dropdown-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              Settings
            </Link>
          </div>

          <div className="dropdown-divider" />

          <button onClick={handleLogout} className="dropdown-link logout-link">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

function formatNotificationTitle(email: Email) {
  const sender = email.sender?.email || 'Olitask';
  return `Email from ${sender}`;
}

function formatEmailTime(timestamp?: string) {
  if (!timestamp) return 'No timestamp';

  const then = new Date(timestamp).getTime();
  if (Number.isNaN(then)) return timestamp;

  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'Just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return new Date(timestamp).toLocaleDateString();
}

export default UserMenu;
